import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Stack, Typography } from '@mui/material';
import Colors from '../../../../library/Colors';
import { useAnchor } from '../../search/WebshopConfigSearch';
import { usePermissions } from '../../../../library/Permissions';
import { cardKeyForAnchor } from '../../../../library/permissionCatalog';
import { useDesignVariant } from '../../_designVariant';

/**
 * Lightweight wrapper used by every per-section config card. Centralises the
 * Paper styling, title+description header and inner spacing so each card file
 * only has to focus on its own fields. Pass `sx` to override per-card.
 *
 * `headerActions` renders inline on the right of the title — handy for
 * search boxes / "Add new" buttons that would otherwise eat a full row.
 *
 * `anchor` registers this card with the config search. When a
 * search hit dispatches the matching anchor id, the card scrolls into view
 * and briefly flashes a blue ring to draw the eye.
 */
// F10: frozen per-theme tokens at module scope. Built inline they were fresh
// objects per render, and every card in an open modal re-renders per
// keystroke — so emotion re-serialised each card's ~20-property sx each time.
const CARD_THEME = {
    light: { cardBg: '#ffffff', borderCol: '#e4e7eb', textColor: Colors.light_text, mutedText: '#768390' },
    dark:  { cardBg: '#2d333b', borderCol: '#3a3f47', textColor: Colors.dark_text,  mutedText: '#b0b8c1' },
};

const ConfigCard = ({ anchor, title, description, headerActions, theme = 'light', children, sx }) => {
    const isDark = theme === 'dark';
    const { cardBg, borderCol, textColor, mutedText } = CARD_THEME[isDark ? 'dark' : 'light'];

    const flash = useAnchor(anchor);
    // Design variant (classic / doorloop) — see _designVariant.js. The
    // tokens are stable per (variant, mode), so they slot into the memo deps.
    const { tokens } = useDesignVariant();
    // Stable sx per (theme, flash, sx-override) — the flash ring is the only
    // state-dependent bit, so quiet renders reuse the exact same object.
    const paperSx = useMemo(() => ({
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${borderCol}`,
        backgroundColor: cardBg,
        boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.30)' : '0 1px 2px rgba(16,24,40,0.05)',
        transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
        '&:hover': {
            boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.45)' : '0 6px 20px rgba(16,24,40,0.10)',
            borderColor: isDark ? '#4a5159' : '#d0d5dd',
        },
        ...(tokens.card || {}),
        ...(flash && {
            boxShadow: isDark
                ? '0 0 0 3px rgba(245, 184, 75, 0.55)'
                : `0 0 0 3px ${Colors.sft_blue}66`,
            borderColor: isDark ? 'rgba(245, 184, 75, 0.9)' : Colors.sft_blue,
        }),
        ...sx,
    }), [isDark, flash, sx, borderCol, cardBg, tokens]);
    const headerSx = useMemo(() => ({
        mb: 1.5,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: borderCol,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        ...(tokens.cardHeader || {}),
    }), [borderCol, tokens]);

    // Auto-gate: a card with an `anchor` is a permission node (derived in the
    // catalog from the search index). Hide it when the active role can't see it.
    // Cards without an anchor aren't gateable yet (no stable key) → always shown.
    const { can } = usePermissions();
    const permKey = anchor ? cardKeyForAnchor(anchor) : null;
    if (permKey && !can(permKey)) return null;

    return (
        <Paper id={anchor || undefined} elevation={0} sx={paperSx}>
            <Box sx={headerSx}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textColor, lineHeight: 1.2, ...(tokens.cardTitle || {}) }}>
                        {title}
                    </Typography>
                    {description && (
                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.5, ...(tokens.cardDescription || {}) }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                {headerActions && (
                    <Box sx={{ flexShrink: 0 }}>{headerActions}</Box>
                )}
            </Box>
            <Stack spacing={tokens.cardBodySpacing} sx={tokens.cardBody || undefined}>
                {children}
            </Stack>
        </Paper>
    );
};

ConfigCard.propTypes = {
    anchor:        PropTypes.string,
    title:         PropTypes.node.isRequired,
    description:   PropTypes.node,
    headerActions: PropTypes.node,
    theme:         PropTypes.string,
    children:      PropTypes.node,
    sx:            PropTypes.object,
};

export default ConfigCard;
